import { Route, Routes, useNavigate } from 'react-router-dom';
import Splash from './views/Public/Splash';
import SignIn from './views/Public/SignIn';
import ResetPassword from './views/Public/ResetPassword';
import InitialSignIn from './views/Public/InitialSignIn';
import { NotAuthorised, NotFound, ProtectedRoleRoute } from '@blumtechgroup/blum-react-core-components';
import { adminRoles, AuthContext, clinicianRoles, locRoles, orgRoles } from './components/AuthProvider';
import { useContext } from 'react';
import DansFundQuestionnaireComplete from './views/Public/DansFund/Questionnaire';
import DansFundOptOut from './views/Public/DansFund/OptOut';
import AdminDansFundQuestionnaire from './views/Admin/DansFundQuestionnaire';
import AdminViewDansFundQuestionnaire from './views/Admin/DansFundQuestionnaire/View';
import CreateDansFundQuestionnaire from './views/Admin/DansFundQuestionnaire/Create';
import EditDansFundQuestionnaire from './views/Admin/DansFundQuestionnaire/Edit';
import OrganisationDansFundQuestionnaire from './views/Organisation/DansFundQuestionnaire';

// Admin view
import {
  ApproveRequestedUser,
  CreateAdminUser as AdminCreateUsers,
  CreateCliniciansUsers as AdminCreateClinicianUsers,
  CreateLicenseTypes as AdminCreateLicenseTypes,
  CreateLocations as AdminCreateLocations,
  CreateLocationUsers as AdminCreateLocationUsers,
  CreateOrganisations as AdminCreateOrganisations,
  CreateOrganisationUsers as AdminCreateOrganisationUsers,
  EditAdminUser as AdminEditUsers,
  EditCliniciansUsers as AdminEditCliniciansUsers,
  EditLicenseTypes as AdminEditLicenseTypes,
  EditLocations as AdminEditLocations,
  EditLocationUsers as AdminEditLocationUsers,
  EditOrganisations as AdminEditOrganisations,
  EditOrganisationsUsers as AdminEditOrganisationsUsers,
  Home as AdminHome,
  ListAdminUsers as AdminListUsers,
  ListClinicianUsers as AdminListClinicianUsers,
  ListLicenseTypes as AdminListLicenseTypes,
  ListLocations as AdminListLocations,
  ListLocationUsers as AdminListLocationUsers,
  ListOrganisations as AdminListOrganisations,
  ListOrganisationUsers as AdminListOrganisationUsers,
  ListRequestedUsers,
  ListTbsaResults as AdminListTbsaResults,
  UserLookup as AdminUserLookup,
  ViewAdminUser as AdminViewUsers,
  ViewClinicianUsers as AdminViewClinicianUsers,
  ViewLicenseTypes as AdminViewLicenseTypes,
  ViewLocationLicenses as AdminViewLocationLicenses,
  ViewLocations as AdminViewLocations,
  ViewLocationUsers as AdminViewLocationUsers,
  ViewOrganisationLicenses as AdminViewOrganisationLicenses,
  ViewOrganisations as AdminViewOrganisations,
  ViewOrganisationUsers as AdminViewOrganisationUsers,
} from './views/Admin';

// Organisation view
import {
  CreateClinicianUsers as OrgCreateClinicianUsers,
  CreateLocation as OrgCreateLocation,
  CreateLocationUsers as OrgCreateLocationUsers,
  CreateOrganisationUsers as OrgCreateOrganisationUsers,
  EditClinicianUsers as OrgEditClinicianUsers,
  EditLocation as OrgEditLocation,
  EditLocationUsers as OrgEditLocationUsers,
  EditOrganisationUsers as OrgEditOrganisationUsers,
  Home as OrgHome,
  ListClinicianUsers as OrgListClinicianUsers,
  ListLicenseTypes as OrgListLicenseTypes,
  ListLocations as OrgListLocations,
  ListLocationUsers as OrgListLocationUsers,
  ListOrganisationUsers as OrgListOrganisationUsers,
  ListTbsaResults as OrgListTbsaResults,
  ViewClinicianUsers as OrgViewClinicianUsers,
  ViewLocation as OrgViewLocation,
  ViewLocationLicenses as OrgViewLocationLicenses,
  ViewLocationUsers as OrgViewLocationUsers,
  ViewOrganisationLicenses as OrgViewOrganisationLicenses,
  ViewOrganisationUsers as OrgViewOrganisationUsers,
} from './views/Organisation';

// Location view
import {
  CreateClinicianUsers as LocCreateClinicianUsers,
  CreateLocationUsers as LocCreateLocationUsers,
  EditClinicianUsers as LocEditClinicianUsers,
  EditLocationUsers as LocEditLocationUsers,
  Home as LocHome,
  ListClinicianUsers as LocListClinicianUsers,
  ListLocationUsers as LocListLocationUsers,
  ListTbsaResults as LocListTbsaResults,
  ViewClinicianUsers as LocViewClinicianUsers,
  ViewLocationUsers as LocViewLocationUsers,
} from './views/Location';

// Clinician view
import { Home as ClinicianHome } from './views/Clinician';

const AppRoutes = () => {
  const navigate = useNavigate();

  const authContext = useContext(AuthContext);
  if (authContext === null) {
    throw new Error('No AuthContext');
  }

  const { userRole } = authContext;

  return (
    <Routes>
      <Route path="/" element={<Splash />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/initial-sign-in" element={<InitialSignIn />} />
      <Route path="/dans-fund-questionnaire/:token" element={<DansFundQuestionnaireComplete />} />
      <Route path="/dans-fund/opt-out/:token" element={<DansFundOptOut />} />

      {/* admin view */}
      <Route element={<ProtectedRoleRoute fallbackComponent={<NotAuthorised goBack={navigate} />} allowedRoles={adminRoles} userRole={userRole} />}>
        <Route path="/admin/home" element={<AdminHome />} />

        {/* user lookup */}
        <Route path="/admin/user-lookup" element={<AdminUserLookup />} />

        {/* users */}
        <Route path="/admin/users" element={<AdminListUsers />} />
        <Route path="/admin/users/:admin_user_id" element={<AdminViewUsers />} />
        <Route path="/admin/users/create" element={<AdminCreateUsers />} />
        <Route path="/admin/users/:admin_user_id/edit" element={<AdminEditUsers />} />

        {/* requested users */}
        <Route path="/admin/requested-users" element={<ListRequestedUsers />} />
        <Route path="/admin/requested-users/:email/approve" element={<ApproveRequestedUser />} />

        {/* organisations */}
        <Route path="/admin/organisations" element={<AdminListOrganisations />} />
        <Route path="/admin/organisations/:organisation_id" element={<AdminViewOrganisations />} />
        <Route path="/admin/organisations/create" element={<AdminCreateOrganisations />} />
        <Route path="/admin/organisations/:organisation_id/edit" element={<AdminEditOrganisations />} />

        {/* organisation users */}
        <Route path="/admin/organisations/:organisation_id/users" element={<AdminListOrganisationUsers />} />
        <Route path="/admin/organisations/:organisation_id/users/:user_id" element={<AdminViewOrganisationUsers />} />
        <Route path="/admin/organisations/:organisation_id/users/create" element={<AdminCreateOrganisationUsers />} />
        <Route path="/admin/organisations/:organisation_id/users/:user_id/edit" element={<AdminEditOrganisationsUsers />} />

        {/* locations */}
        <Route path="/admin/organisations/:organisation_id/locations" element={<AdminListLocations />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id" element={<AdminViewLocations />} />
        <Route path="/admin/organisations/:organisation_id/locations/create" element={<AdminCreateLocations />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/edit" element={<AdminEditLocations />} />

        {/* location users */}
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/users" element={<AdminListLocationUsers />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/users/:user_id" element={<AdminViewLocationUsers />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/users/create" element={<AdminCreateLocationUsers />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/users/:user_id/edit" element={<AdminEditLocationUsers />} />

        {/* clinicians */}
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/clinicians" element={<AdminListClinicianUsers />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/clinicians/:clinician_id" element={<AdminViewClinicianUsers />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/clinicians/create" element={<AdminCreateClinicianUsers />} />
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/clinicians/:clinician_id/edit" element={<AdminEditCliniciansUsers />} />

        {/* license types */}
        <Route path="/admin/license-types" element={<AdminListLicenseTypes />} />
        <Route path="/admin/license-types/:license_type_id" element={<AdminViewLicenseTypes />} />
        <Route path="/admin/license-types/create" element={<AdminCreateLicenseTypes />} />
        <Route path="/admin/license-types/:license_type_id/edit" element={<AdminEditLicenseTypes />} />

        {/* Dan's Fund Questionnaire */}
        <Route path="/admin/dans-fund-questionnaire" element={<AdminDansFundQuestionnaire />} />
        <Route path="/admin/dans-fund-questionnaire/create" element={<CreateDansFundQuestionnaire />} />
        <Route path="/admin/dans-fund-questionnaire/:id" element={<AdminViewDansFundQuestionnaire />} />
        <Route path="/admin/dans-fund-questionnaire/:id/edit" element={<EditDansFundQuestionnaire />} />

        {/* licenses */}
        <Route path="/admin/license-types/:license_type_id/organisations/:organisation_id" element={<AdminViewOrganisationLicenses />} />
        <Route path="/admin/license-types/:license_type_id/organisations/:organisation_id/locations/:location_id" element={<AdminViewLocationLicenses />} />

        {/* TBSA results */}
        <Route path="/admin/organisations/:organisation_id/locations/:location_id/tbsa" element={<AdminListTbsaResults />} />
      </Route>

      {/* organisation view */}
      <Route element={<ProtectedRoleRoute fallbackComponent={<NotAuthorised goBack={navigate} />} allowedRoles={orgRoles} userRole={userRole} />}>
        <Route path="/org/home" element={<OrgHome />} />

        {/* organisation users */}
        <Route path="/org/users" element={<OrgListOrganisationUsers />} />
        <Route path="/org/users/:user_id" element={<OrgViewOrganisationUsers />} />
        <Route path="/org/users/create" element={<OrgCreateOrganisationUsers />} />
        <Route path="/org/users/:user_id/edit" element={<OrgEditOrganisationUsers />} />

        {/* locations */}
        <Route path="/org/locations" element={<OrgListLocations />} />
        <Route path="/org/locations/:location_id" element={<OrgViewLocation />} />
        <Route path="/org/locations/create" element={<OrgCreateLocation />} />
        <Route path="/org/locations/:location_id/edit" element={<OrgEditLocation />} />

        {/* location users */}
        <Route path="/org/locations/:location_id/users" element={<OrgListLocationUsers />} />
        <Route path="/org/locations/:location_id/users/:user_id" element={<OrgViewLocationUsers />} />
        <Route path="/org/locations/:location_id/users/create" element={<OrgCreateLocationUsers />} />
        <Route path="/org/locations/:location_id/users/:user_id/edit" element={<OrgEditLocationUsers />} />

        {/* clinicians */}
        <Route path="/org/locations/:location_id/clinicians" element={<OrgListClinicianUsers />} />
        <Route path="/org/locations/:location_id/clinicians/:clinician_id" element={<OrgViewClinicianUsers />} />
        <Route path="/org/locations/:location_id/clinicians/create" element={<OrgCreateClinicianUsers />} />
        <Route path="/org/locations/:location_id/clinicians/:clinician_id/edit" element={<OrgEditClinicianUsers />} />

        {/* license types */}
        <Route path="/org/license-types" element={<OrgListLicenseTypes />} />

        {/* Dan's Fund Questionnaire */}
        <Route path="/org/dans-fund-questionnaire" element={<OrganisationDansFundQuestionnaire />} />

        {/* licenses */}
        <Route path="/org/license-types/:license_type_id" element={<OrgViewOrganisationLicenses />} />
        <Route path="/org/license-types/:license_type_id/locations/:location_id" element={<OrgViewLocationLicenses />} />

        {/* tbsa results */}
        <Route path="/org/locations/:location_id/tbsa" element={<OrgListTbsaResults />} />
      </Route>

      {/* location view */}
      <Route element={<ProtectedRoleRoute fallbackComponent={<NotAuthorised goBack={navigate} />} allowedRoles={locRoles} userRole={userRole} />}>
        <Route path="/loc/home" element={<LocHome />} />

        {/* location users */}
        <Route path="/loc/users" element={<LocListLocationUsers />} />
        <Route path="/loc/users/:user_id" element={<LocViewLocationUsers />} />
        <Route path="/loc/users/create" element={<LocCreateLocationUsers />} />
        <Route path="/loc/users/:user_id/edit" element={<LocEditLocationUsers />} />

        {/* clinicians */}
        <Route path="/loc/clinicians" element={<LocListClinicianUsers />} />
        <Route path="/loc/clinicians/:clinician_id" element={<LocViewClinicianUsers />} />
        <Route path="/loc/clinicians/create" element={<LocCreateClinicianUsers />} />
        <Route path="/loc/clinicians/:clinician_id/edit" element={<LocEditClinicianUsers />} />

        {/* tbsa results */}
        <Route path="/loc/tbsa" element={<LocListTbsaResults />} />
      </Route>

      {/* clinician view */}
      <Route element={<ProtectedRoleRoute fallbackComponent={<NotAuthorised goBack={navigate} />} allowedRoles={clinicianRoles} userRole={userRole} />}>
        <Route path="/clinician/home" element={<ClinicianHome />} />
      </Route>

      <Route path="*" element={<NotFound goBack={navigate} />} />
    </Routes>
  );
};

export default AppRoutes;
